// WhatsApp deep-link + image-share helpers for sales receipts.
// - For text-only quick share: `wa.me/{phone}?text=…`
// - For image share: render receipt DOM → PNG, then either
//   (a) use the Web Share API (preferred — opens native share sheet so the user picks WhatsApp), or
//   (b) download the PNG and open the WhatsApp chat so the user attaches it manually.

import { toPng } from "html-to-image";
import { toast } from "sonner";

const MODE_LABEL = {
  cash: "Cash",
  upi: "UPI",
  card: "Card",
  credit: "Credit",
};

// Strip non-digits and prepend India country code (91) for plain 10-digit numbers.
export function normalizePhone(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return "91" + digits;
  return digits;
}

export function buildReceiptText(sale, brandName = "DYYNIT INTEL HITMAN") {
  const lines = [];
  lines.push(`*${brandName}*`);
  lines.push(`Receipt #${(sale.id || "").slice(0, 8).toUpperCase()}`);
  if (sale.type) lines.push(`Type: ${String(sale.type).toUpperCase()}`);
  if (sale.customer_name) lines.push(`Customer: ${sale.customer_name}`);
  lines.push("");
  lines.push("--- ITEMS ---");
  (sale.line_items || []).forEach((l) => {
    const p = l.product_snapshot || {};
    const qty = Number(l.quantity || 0);
    const mrp = Number(l.mrp || 0);
    const lineTotal = (qty * mrp).toFixed(2);
    lines.push(`• ${p.product || "Item"} × ${qty} @ ₹${mrp.toFixed(2)} = ₹${lineTotal}`);
  });
  lines.push("");
  const mrpTotal = (sale.line_items || [])
    .reduce((a, l) => a + Number(l.mrp || 0) * Number(l.quantity || 0), 0)
    .toFixed(2);
  lines.push(`Total (MRP): ₹${mrpTotal}`);
  lines.push(`Amount payable: *₹${Number(sale.total || 0).toFixed(2)}*`);
  if (sale.payment_mode) {
    lines.push(`Paid via: ${MODE_LABEL[sale.payment_mode] || sale.payment_mode}`);
  }
  if (sale.payment_status) {
    lines.push(`Status: ${String(sale.payment_status).toUpperCase()}`);
  }
  if (sale.notes) {
    lines.push("");
    lines.push(`Note: ${sale.notes}`);
  }
  lines.push("");
  lines.push("Thank you — visit again!");
  return lines.join("\n");
}

export function buildWhatsAppUrl(phone, text) {
  const p = normalizePhone(phone);
  const encoded = encodeURIComponent(text || "");
  return p ? `https://wa.me/${p}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
}

// Text-only share (legacy quick action)
export function shareSaleOnWhatsApp(sale, brandName) {
  const text = buildReceiptText(sale, brandName);
  const phone = sale?.customer_snapshot?.phone1 || sale?.customer_snapshot?.phone2 || "";
  const url = buildWhatsAppUrl(phone, text);
  window.open(url, "_blank", "noopener");
}

// Convert the live receipt DOM node to a PNG Blob.
export async function receiptNodeToPngBlob(node) {
  if (!node) throw new Error("Receipt node not found");
  const dataUrl = await toPng(node, {
    pixelRatio: 2,
    backgroundColor: "#ffffff",
    cacheBust: true,
    style: { margin: "0" },
  });
  const res = await fetch(dataUrl);
  return res.blob();
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// Send the receipt as a PNG image via WhatsApp.
// - Tries Web Share API with files first (mobile + modern desktop browsers will show WhatsApp in the share sheet).
// - Falls back to downloading the PNG and opening the WhatsApp chat so the user can attach it in one tap.
export async function shareReceiptImage(sale, receiptNode, brandName) {
  try {
    const blob = await receiptNodeToPngBlob(receiptNode);
    const shortId = (sale?.id || "receipt").slice(0, 8).toUpperCase();
    const filename = `receipt-${shortId}.png`;
    const file = new File([blob], filename, { type: "image/png" });

    const phone = sale?.customer_snapshot?.phone1 || sale?.customer_snapshot?.phone2 || "";
    const text = `${brandName || "Receipt"} · #${shortId} · ₹${Number(sale?.total || 0).toFixed(2)}`;

    // Web Share API path (mobile / modern browsers)
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.canShare === "function" &&
      navigator.canShare({ files: [file] }) &&
      typeof navigator.share === "function"
    ) {
      try {
        await navigator.share({ files: [file], title: filename, text });
        return { method: "share" };
      } catch (e) {
        if (e && e.name === "AbortError") return { method: "abort" };
        // fall through to download fallback
      }
    }

    // Fallback: download image + open the WhatsApp chat so the user attaches it.
    downloadBlob(blob, filename);
    const url = buildWhatsAppUrl(phone, text);
    window.open(url, "_blank", "noopener");
    toast.success("Receipt image downloaded — attach it in the WhatsApp chat that just opened.");
    return { method: "download" };
  } catch (err) {
    console.error("shareReceiptImage failed", err);
    toast.error("Could not generate receipt image. Please try again.");
    return { method: "error", error: err };
  }
}
